import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, useBusinessTypes } from "@/components/app/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, Shield, Users as UsersIcon, Pencil, KeyRound, LogIn, Search, Briefcase, User as UserIcon } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";

const ROLES_BISNIS = [{v:"admin",l:"Admin (Owner Workspace)"},{v:"finance",l:"Finance"},{v:"super_admin",l:"Super Admin"}];
const ROLES_PRIBADI = [{v:"admin",l:"Admin (Pemilik)"},{v:"finance",l:"Finance"}];
const STATUSES = [{v:"active",l:"Aktif"},{v:"inactive",l:"Nonaktif"},{v:"pending_approval",l:"Menunggu Persetujuan"},{v:"pending_verification",l:"Belum Verifikasi"}];
const isPribadiUser = (u) => u.role !== "super_admin" && u.workspace_type === "pribadi";

const statusCls = (s) => s === "active" ? "bg-emerald-100 text-emerald-700"
  : s === "pending_verification" ? "bg-amber-100 text-amber-700"
  : s === "pending_approval" ? "bg-blue-100 text-blue-700" : "bg-rose-100 text-rose-700";

function PwInput({ value, onChange, testid, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={onChange} data-testid={testid} placeholder={placeholder} autoComplete="new-password"/>
      <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500" tabIndex={-1}>{show ? <EyeOff size={16}/> : <Eye size={16}/>}</button>
    </div>
  );
}

export default function SuperAdminUsers() {
  const { impersonate } = useAuth();
  const [tab, setTab] = useState("bisnis");
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [menus, setMenus] = useState({});
  const [q, setQ] = useState("");
  const bizTypes = useBusinessTypes();
  const [open, setOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [assignMode, setAssignMode] = useState("new");
  const emptyForm = (t) => ({ name:"", email:"", no_telp:"", password:"", confirm:"", role:"admin", workspace_id:"",
    workspace_type: t === "pribadi" ? "pribadi" : "hostnesia", workspace_name:"" });
  const [form, setForm] = useState(emptyForm("bisnis"));

  const load = async () => {
    const [u, w] = await Promise.all([api.get("/users"), api.get("/workspaces")]);
    setUsers(u.data); setWorkspaces(w.data);
  };
  useEffect(() => { load(); api.get("/menus").then((r) => setMenus(r.data)).catch(() => {}); }, []);

  const tabUsers = useMemo(() => users.filter((u) => (tab === "pribadi") === isPribadiUser(u)), [users, tab]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return tabUsers;
    return tabUsers.filter((u) => [u.name, u.email, u.workspace_name, u.no_telp].some((x) => (x || "").toLowerCase().includes(s)));
  }, [tabUsers, q]);
  const tabWorkspaces = useMemo(() => workspaces.filter((w) => (tab === "pribadi") === (w.type === "pribadi")), [workspaces, tab]);
  const bizTypesBisnis = bizTypes.filter((t) => t.code !== "pribadi");
  const typeLabel = (code) => bizTypes.find((x) => x.code === code)?.label || code;
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(filtered, 10);
  useEffect(() => { setPage(1); }, [tab, q, setPage]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const openCreate = () => { setForm(emptyForm(tab)); setAssignMode(tab === "pribadi" ? "new" : "new"); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Nama & email wajib diisi"); return; }
    if (form.password.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    if (form.password !== form.confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    try {
      const { confirm, ...rest } = form;
      const payload = { ...rest };
      if (rest.role === "super_admin") {
        payload.workspace_id = null; payload.workspace_type = null; payload.workspace_name = null;
      } else if (assignMode === "existing") {
        if (!rest.workspace_id) { toast.error("Pilih workspace"); return; }
        payload.workspace_type = undefined; payload.workspace_name = undefined;
      } else {
        payload.workspace_id = undefined;
        if (tab === "pribadi") payload.workspace_type = "pribadi";
      }
      await api.post("/users", payload);
      setOpen(false); load(); toast.success("User dibuat");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Hapus user ${u.email}? Data transaksi workspace tidak ikut terhapus.`)) return;
    try { await api.delete(`/users/${u.id}`); load(); toast.success("Terhapus"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.id}/role`, { role }); load(); toast.success("Peran diubah"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const approveUser = async (u, approve) => {
    try { await api.post(`/users/${u.id}/approve`, { approve }); toast.success(approve ? "User disetujui" : "User ditolak"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const delWorkspace = async (w) => {
    if (!window.confirm(`Hapus workspace "${w.name}"? Semua data (user, pemasukan, pengeluaran, produksi, dll) di workspace ini akan ikut terhapus permanen.`)) return;
    try { await api.delete(`/workspaces/${w.id}`); toast.success("Workspace terhapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Permission ----
  const permMenus = permUser ? (menus[permUser.workspace_type] || menus.hostnesia || []) : [];
  const openPerm = (u) => {
    const list = menus[u.workspace_type] || menus.hostnesia || [];
    setPermUser({ ...u, _sel: u.menu_permissions == null ? list.map((m) => m.k) : [...u.menu_permissions] });
  };
  const togglePerm = (k) => {
    const p = new Set(permUser._sel);
    p.has(k) ? p.delete(k) : p.add(k);
    setPermUser({ ...permUser, _sel: Array.from(p) });
  };
  const savePerms = async () => {
    if (!permUser._sel.length) { toast.error("Pilih minimal satu menu"); return; }
    try {
      await api.put(`/users/${permUser.id}/permissions`, { menu_permissions: permUser._sel });
      toast.success("Permission disimpan"); setPermUser(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const permGroups = useMemo(() => {
    const g = [];
    permMenus.forEach((m) => { let x = g.find((y) => y.name === m.g); if (!x) { x = { name: m.g, items: [] }; g.push(x); } x.items.push(m); });
    return g;
  }, [permMenus]);

  // ---- Edit ----
  const openEdit = (u) => setEditUser({ id: u.id, name: u.name || "", email: u.email, no_telp: u.no_telp || "", status: u.status || "active",
    workspace_id: u.workspace_id || "", _orig: u });
  const saveEdit = async () => {
    const o = editUser._orig;
    const body = { name: editUser.name, email: editUser.email, no_telp: editUser.no_telp, status: editUser.status };
    if (o.role !== "super_admin" && editUser.workspace_id && editUser.workspace_id !== o.workspace_id) body.workspace_id = editUser.workspace_id;
    try { await api.put(`/users/${editUser.id}`, body); toast.success("Data user diperbarui"); setEditUser(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const editWsOptions = editUser ? workspaces.filter((w) => (w.type === "pribadi") === (editUser._orig.workspace_type === "pribadi")) : [];

  // ---- Password ----
  const savePw = async () => {
    if (pw.password.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    if (pw.password !== pw.confirm) { toast.error("Konfirmasi password tidak cocok"); return; }
    try { await api.put(`/users/${pwUser.id}/password`, { password: pw.password }); toast.success(`Password ${pwUser.email} diganti`); setPwUser(null); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Masuk sebagai ----
  const loginAs = async (u) => {
    if (!window.confirm(`Masuk sebagai ${u.name || u.email}? Anda bisa kembali ke Super Admin lewat tombol di bagian atas.`)) return;
    try { const { data } = await api.post(`/users/${u.id}/impersonate`); impersonate(data.token); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const wsLabel = (u) => {
    if (!u.workspace_id) return <span className="text-slate-400 italic">Global</span>;
    const w = workspaces.find((x) => x.id === u.workspace_id);
    return <span>{w?.name || u.workspace_name || u.workspace_id}<span className="block text-[10px] text-slate-400">{typeLabel(w?.type || u.workspace_type)}</span></span>;
  };
  const roles = tab === "pribadi" ? ROLES_PRIBADI : ROLES_BISNIS;

  return (<>
    <PageHeader title="Manajemen User (Super Admin)" subtitle="Kelola user Bisnis & Pribadi: data akun, password, permission menu, dan masuk sebagai user."
      actions={<Button onClick={openCreate} className="brand-btn rounded-sm" data-testid="add-user"><Plus size={14} className="mr-1"/>Tambah User {tab === "pribadi" ? "Pribadi" : "Bisnis"}</Button>}/>
    <div className="p-4 md:p-8 space-y-4">
      <div className="inline-grid grid-cols-2 p-1 bg-slate-100 rounded-sm" role="tablist" data-testid="sa-user-tabs">
        {[["bisnis", "Bisnis", Briefcase], ["pribadi", "Pribadi", UserIcon]].map(([k, l, Icon]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`sa-tab-${k}`}
            className={`px-5 py-2 text-sm font-medium rounded-sm flex items-center gap-2 transition-colors duration-150 ${tab === k ? "bg-white shadow-sm text-[color:var(--brand)]" : "text-slate-500 hover:text-slate-800"}`}>
            <Icon size={14}/>{l}<span className="text-[11px] text-slate-400">({users.filter((u) => (k === "pribadi") === isPribadiUser(u)).length})</span>
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="card-flat p-4"><div className="text-xs text-slate-500">User {tab === "pribadi" ? "Pribadi" : "Bisnis"}</div><div className="text-2xl font-semibold" data-testid="sa-stat-users">{tabUsers.length}</div></div>
        <div className="card-flat p-4"><div className="text-xs text-slate-500">Workspace {tab === "pribadi" ? "Pribadi" : "Bisnis"}</div><div className="text-2xl font-semibold">{tabWorkspaces.length}</div></div>
        <div className="card-flat p-4"><div className="text-xs text-slate-500">{tab === "pribadi" ? "User Aktif" : "Super Admin"}</div><div className="text-2xl font-semibold">{tab === "pribadi" ? tabUsers.filter((u) => u.status === "active").length : tabUsers.filter((u) => u.role === "super_admin").length}</div></div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="p-3 border-b flex items-center gap-2">
          <Search size={14} className="text-slate-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama, email, workspace…" className="h-8 max-w-xs" data-testid="sa-user-search"/>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="sa-users-table">
          <thead>
            <tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
              <th className="p-3">Nama</th><th className="p-3">Email</th><th className="p-3">Peran</th>
              <th className="p-3">Workspace</th><th className="p-3">Menu</th><th className="p-3">Status</th><th className="p-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-slate-400">Belum ada user.</td></tr>}
            {paged.map((u) => {
              const list = menus[u.workspace_type] || [];
              return (
              <tr key={u.id} className="border-b hover:bg-slate-50" data-testid={`sa-user-row-${u.email}`}>
                <td className="p-3 font-medium">{u.name}{u.no_telp && <span className="block text-[11px] text-slate-400 font-normal">{u.no_telp}</span>}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3">
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                    <SelectTrigger className="h-8 w-48" data-testid={`sa-role-${u.email}`}><SelectValue/></SelectTrigger>
                    <SelectContent>{(isPribadiUser(u) ? ROLES_PRIBADI : ROLES_BISNIS).map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-[12px]">{wsLabel(u)}</td>
                <td className="p-3 text-[11px] text-slate-500" data-testid={`sa-menu-count-${u.email}`}>
                  {u.role === "super_admin" ? "—" : u.menu_permissions == null ? <span className="text-emerald-700">Semua menu</span> : `${u.menu_permissions.length}/${list.length || "?"} menu`}
                </td>
                <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded-sm ${statusCls(u.status || "active")}`}>{STATUSES.find((s) => s.v === (u.status || "active"))?.l || u.status}</span></td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end items-center">
                    {u.status === "pending_approval" && (
                      <button onClick={() => approveUser(u, true)} className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`sa-approve-${u.email}`}>Setujui</button>
                    )}
                    {u.role !== "super_admin" && u.workspace_id && (
                      <button onClick={() => loginAs(u)} className="p-1.5 hover:bg-blue-50 rounded-sm" title="Masuk sebagai user ini" data-testid={`sa-login-as-${u.email}`}><LogIn size={14} className="text-blue-600"/></button>
                    )}
                    <button onClick={() => openEdit(u)} className="p-1.5 hover:bg-slate-100 rounded-sm" title="Ubah data user" data-testid={`sa-edit-${u.email}`}><Pencil size={14} className="text-slate-600"/></button>
                    <button onClick={() => { setPw({ password: "", confirm: "" }); setPwUser(u); }} className="p-1.5 hover:bg-amber-50 rounded-sm" title="Ganti password" data-testid={`sa-pw-${u.email}`}><KeyRound size={14} className="text-amber-600"/></button>
                    {u.role !== "super_admin" && (
                      <button onClick={() => openPerm(u)} className="p-1.5 hover:bg-slate-100 rounded-sm" title="Atur Menu" data-testid={`sa-perm-${u.email}`}><Shield size={14} className="brand-text"/></button>
                    )}
                    <button onClick={() => del(u)} className="p-1.5 hover:bg-rose-50 rounded-sm" title="Hapus user" data-testid={`sa-del-${u.email}`}><Trash2 size={14} className="text-rose-600"/></button>
                  </div>
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="sa-users-pagination"/>
      </div>

      <div className="card-flat p-4">
        <div className="flex items-center gap-2 mb-2"><UsersIcon size={14} className="brand-text"/><h3 className="font-semibold text-sm">Workspace {tab === "pribadi" ? "Pribadi" : "Bisnis"}</h3></div>
        {tabWorkspaces.length === 0 && <div className="text-sm text-slate-400">Belum ada workspace.</div>}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {tabWorkspaces.map((w) => (
            <div key={w.id} className="border rounded-sm p-3 text-sm flex items-start justify-between gap-2" data-testid={`sa-ws-${w.id}`}>
              <div className="min-w-0">
                <div className="font-semibold truncate">{w.name}</div>
                <div className="text-[11px] text-slate-500">{typeLabel(w.type)} · {users.filter((u) => u.workspace_id === w.id).length} user</div>
                <div className="text-[11px] text-slate-400 font-mono truncate">{w.id}</div>
              </div>
              {w.id !== "ws-hostnesia-default" && (
                <button onClick={() => delWorkspace(w)} className="p-1.5 rounded-sm hover:bg-rose-50 shrink-0" title="Hapus workspace" data-testid={`sa-del-ws-${w.id}`}><Trash2 size={14} className="text-rose-600"/></button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Create user dialog */}
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Tambah User {tab === "pribadi" ? "Pribadi" : "Bisnis"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama Lengkap *</Label><Input value={form.name} onChange={upd("name")} data-testid="sa-form-name"/></div>
          <div><Label>Email *</Label><Input type="email" value={form.email} onChange={upd("email")} data-testid="sa-form-email"/></div>
          <div><Label>No. Telp</Label><Input value={form.no_telp} onChange={upd("no_telp")} data-testid="sa-form-telp"/></div>
          <div>
            <Label>Peran</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="sa-form-role"><SelectValue/></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.role !== "super_admin" && (
            <div className="border rounded-sm p-3 space-y-3 bg-slate-50/60">
              <div className="flex flex-wrap gap-3 text-sm">
                <label className="flex items-center gap-2"><input type="radio" checked={assignMode === "new"} onChange={() => setAssignMode("new")} data-testid="sa-form-mode-new"/> Buat workspace baru</label>
                <label className="flex items-center gap-2"><input type="radio" checked={assignMode === "existing"} onChange={() => setAssignMode("existing")} data-testid="sa-form-mode-existing"/> Gabungkan ke workspace</label>
              </div>
              {assignMode === "existing" ? (
                <div>
                  <Label>Pilih Workspace</Label>
                  <Select value={form.workspace_id} onValueChange={(v) => setForm({ ...form, workspace_id: v })}>
                    <SelectTrigger data-testid="sa-form-ws"><SelectValue placeholder="Pilih..."/></SelectTrigger>
                    <SelectContent>{tabWorkspaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name} ({typeLabel(w.type)})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-3">
                  {tab === "bisnis" && (
                    <div>
                      <Label>Jenis Bisnis</Label>
                      <Select value={form.workspace_type} onValueChange={(v) => setForm({ ...form, workspace_type: v })}>
                        <SelectTrigger data-testid="sa-form-wstype"><SelectValue/></SelectTrigger>
                        <SelectContent>{bizTypesBisnis.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label>Nama Workspace</Label><Input value={form.workspace_name} onChange={upd("workspace_name")} placeholder={tab === "pribadi" ? "mis. Keuangan Pribadi Budi" : "mis. Cabang Malang"} data-testid="sa-form-wsname"/></div>
                </div>
              )}
            </div>
          )}
          <div><Label>Password *</Label><PwInput value={form.password} onChange={upd("password")} testid="sa-form-password"/></div>
          <div><Label>Konfirmasi Password *</Label><PwInput value={form.confirm} onChange={upd("confirm")} testid="sa-form-confirm"/></div>
        </div>
        <DialogFooter data-dialog-footer>
          <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
          <Button onClick={save} className="brand-btn" data-testid="sa-form-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Edit user dialog */}
    <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Ubah Data User</DialogTitle></DialogHeader>
        {editUser && (
          <div className="space-y-3">
            <div><Label>Nama Lengkap *</Label><Input value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} data-testid="sa-edit-name"/></div>
            <div><Label>Email (untuk login) *</Label><Input type="email" value={editUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} data-testid="sa-edit-email"/></div>
            <div><Label>No. Telp</Label><Input value={editUser.no_telp} onChange={(e) => setEditUser({ ...editUser, no_telp: e.target.value })} data-testid="sa-edit-telp"/></div>
            <div>
              <Label>Status Akun</Label>
              <Select value={editUser.status} onValueChange={(v) => setEditUser({ ...editUser, status: v })}>
                <SelectTrigger data-testid="sa-edit-status"><SelectValue/></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editUser._orig.role !== "super_admin" && editUser._orig.workspace_id && (
              <div>
                <Label>Workspace</Label>
                <Select value={editUser.workspace_id} onValueChange={(v) => setEditUser({ ...editUser, workspace_id: v })}>
                  <SelectTrigger data-testid="sa-edit-ws"><SelectValue/></SelectTrigger>
                  <SelectContent>{editWsOptions.map((w) => <SelectItem key={w.id} value={w.id}>{w.name} ({typeLabel(w.type)})</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[11px] text-slate-400 mt-1">Pindah workspace hanya sesama {isPribadiUser(editUser._orig) ? "Pribadi" : "Bisnis"}. Data transaksi tetap di workspace asalnya.</p>
              </div>
            )}
          </div>
        )}
        <DialogFooter data-dialog-footer>
          <Button variant="outline" onClick={() => setEditUser(null)}>Batal</Button>
          <Button onClick={saveEdit} className="brand-btn" data-testid="sa-edit-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Password dialog */}
    <Dialog open={!!pwUser} onOpenChange={(v) => !v && setPwUser(null)}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Ganti Password — {pwUser?.email}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Untuk user yang lupa password. Password lama langsung tidak berlaku dan kunci akun (jika ada) dibuka.</p>
        <div className="space-y-3">
          <div><Label>Password Baru *</Label><PwInput value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} testid="sa-pw-new" placeholder="Minimal 6 karakter"/></div>
          <div><Label>Konfirmasi Password *</Label><PwInput value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} testid="sa-pw-confirm"/></div>
        </div>
        <DialogFooter data-dialog-footer>
          <Button variant="outline" onClick={() => setPwUser(null)}>Batal</Button>
          <Button onClick={savePw} className="brand-btn" data-testid="sa-pw-save">Simpan Password</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Permissions dialog */}
    <Dialog open={!!permUser} onOpenChange={(v) => !v && setPermUser(null)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Permission Menu — {permUser?.name}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-500">Menu sesuai workspace <b>{typeLabel(permUser?.workspace_type)}</b>. Menu yang tidak dicentang disembunyikan & aksesnya diblokir.</p>
          <div className="flex gap-2">
            <button type="button" className="text-xs brand-text hover:underline" onClick={() => setPermUser({ ...permUser, _sel: permMenus.map((m) => m.k) })} data-testid="perm-all">Pilih semua</button>
            <button type="button" className="text-xs text-slate-500 hover:underline" onClick={() => setPermUser({ ...permUser, _sel: [] })} data-testid="perm-none">Kosongkan</button>
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto space-y-3 pr-1" data-testid="perm-list">
          {permGroups.map((g) => (
            <div key={g.name}>
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">{g.name}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {g.items.map((m) => (
                  <label key={m.k} className="flex items-center gap-2 p-2 border rounded-sm hover:bg-slate-50 cursor-pointer" data-testid={`perm-item-${m.k}`}>
                    <Checkbox checked={(permUser?._sel || []).includes(m.k)} onCheckedChange={() => togglePerm(m.k)} data-testid={`perm-check-${m.k}`}/>
                    <span className="text-sm">{m.l}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-400">Profil & Password selalu tersedia. Manajemen User, Log Aktivitas, Kotak Sampah & Backup otomatis hanya untuk peran Admin.</p>
        <DialogFooter data-dialog-footer>
          <Button variant="outline" onClick={() => setPermUser(null)}>Batal</Button>
          <Button onClick={savePerms} className="brand-btn" data-testid="perm-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}
